// Badges and XP — the game layer's numbers, derived from counts the database
// already keeps rather than stored in a ledger that could drift from them.
// Pure: the core API computes a profile's from its counts (workers/src/users.ts)
// and the web app only renders what comes back.

export interface ProducerStats {
  /** Public seeds uploaded (every take). */
  uploads: number;
  /** Public takes on someone else's tree. */
  remixesMade: number;
  /** Public takes by others hanging directly off this producer's seeds. */
  timesRemixed: number;
  /** Public seeds committed from a jam. */
  jamPublishes: number;
  /** Of those, the ones from a multiplayer jam: a jam that has had two or
   * more different people in it at the same time (`jams.multiplayer_at`). */
  multiplayerJamSeeds: number;
  /** Of the multiplayer-jam seeds, the ones on someone else's tree (also
   * counted in remixesMade). */
  multiplayerJamRemixes: number;
  /** Likes from other people on their public seeds. */
  likesReceived: number;
  /** Distinct listeners on their public seeds, each counted once per seed
   * (a producer's takes on one tree are one seed) and never themselves. */
  listenersReceived: number;
  /** Deepest public tree they started, in generations (a lone seed is 1). */
  deepestTree: number;
}

/**
 * What one event is worth. Each public seed a producer makes earns exactly
 * ONE of the first three: a seed out of a multiplayer jam is 25 in total (not
 * 25 on top of the plant or remix it also is), a remix 20, any other seed 10.
 */
export const XP_WEIGHTS = {
  uploads: 10,
  remixesMade: 20,
  multiplayerJamSeeds: 25,
  timesRemixed: 20,
  likesReceived: 2,
} as const;

/** Listeners earn +1 XP per this many — whole points, never fractions. */
export const LISTENERS_PER_XP = 3;

/** The rules as the charts page lists them, built from the weights above so
 * the page cannot drift from the scoring again. */
export interface XpRule {
  label: string;
  points: number;
}
export const XP_RULES: readonly XpRule[] = [
  { label: "A seed comes out of a multiplayer jam", points: XP_WEIGHTS.multiplayerJamSeeds },
  { label: "Someone remixes your seed", points: XP_WEIGHTS.timesRemixed },
  { label: "You remix someone's seed", points: XP_WEIGHTS.remixesMade },
  { label: "You plant a seed", points: XP_WEIGHTS.uploads },
  { label: "Someone likes your seed", points: XP_WEIGHTS.likesReceived },
  { label: `Every ${LISTENERS_PER_XP} new listeners on your seeds`, points: 1 },
];

export function xpOf(s: ProducerStats): number {
  // Split the producer's seeds into the three kinds, each counted once.
  const remixes = s.remixesMade - s.multiplayerJamRemixes;
  const plants = s.uploads - s.remixesMade - (s.multiplayerJamSeeds - s.multiplayerJamRemixes);
  return (
    plants * XP_WEIGHTS.uploads +
    remixes * XP_WEIGHTS.remixesMade +
    s.multiplayerJamSeeds * XP_WEIGHTS.multiplayerJamSeeds +
    s.timesRemixed * XP_WEIGHTS.timesRemixed +
    s.likesReceived * XP_WEIGHTS.likesReceived +
    Math.floor(s.listenersReceived / LISTENERS_PER_XP)
  );
}

/**
 * Level n needs 50·n·(n−1) XP in total: 0, 100, 300, 600, 1000, … — each
 * level asks 100 more than the last, so early ones come quickly.
 */
export function levelOf(xp: number): { level: number; floor: number; next: number } {
  let level = 1;
  while (50 * (level + 1) * level <= xp) level++;
  return { level, floor: 50 * level * (level - 1), next: 50 * (level + 1) * level };
}

export interface BadgeDef {
  id: string;
  label: string;
  description: string;
  earned: (s: ProducerStats) => boolean;
}

export const BADGES: readonly BadgeDef[] = [
  { id: "first-seed", label: "First Seed", description: "Put a seed up for anyone to hear.", earned: (s) => s.uploads >= 1 },
  { id: "first-remix", label: "First Remix", description: "Made something from someone else's seed.", earned: (s) => s.remixesMade >= 1 },
  { id: "remixed-10", label: "Remixed ×10", description: "Other producers built on your seeds ten times.", earned: (s) => s.timesRemixed >= 10 },
  { id: "five-generations", label: "Five Generations", description: "A seed of yours grew a tree five takes deep.", earned: (s) => s.deepestTree >= 5 },
  { id: "jam-regular", label: "Jam Regular", description: "Ten seeds committed from jams.", earned: (s) => s.jamPublishes >= 10 },
  { id: "crowd-favourite", label: "Crowd Favourite", description: "A hundred likes across your seeds.", earned: (s) => s.likesReceived >= 100 },
];

export function badgesOf(s: ProducerStats): string[] {
  return BADGES.filter((b) => b.earned(s)).map((b) => b.id);
}

export function badgeLabel(id: string | null | undefined): string {
  return BADGES.find((b) => b.id === id)?.label ?? "a badge";
}
